import {
  App,
  Button,
  Card,
  Empty,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type { InputRef } from 'antd';
import { useNavigate } from 'react-router';
import { setupApi } from '@/api/setup';
import { studentsApi, type StudentSummary } from '@/api/students';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';
import { CashierTodayRail } from './cashier-today-rail';
import { CashierQuickActions } from './cashier-quick-actions';

export function FeeCollectPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const yearId = useAuthStore((s) => s.academicYearId);

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [classSectionId, setClassSectionId] = useState<number | undefined>();
  const [page, setPage] = useState(0);
  const [size] = useState(25);
  const [navigating, setNavigating] = useState<number | null>(null);
  const searchRef = useRef<InputRef>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [debounced, classSectionId]);

  // Focus the search on mount. Re-focus when "/" is pressed anywhere on the page
  // (unless the user is already typing in a field).
  useEffect(() => {
    searchRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || t?.isContentEditable) return;
      e.preventDefault();
      searchRef.current?.focus();
      searchRef.current?.select();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const params = {
    q: debounced || undefined,
    classSectionId,
    academicYearId: yearId ?? undefined,
    status: 'ACTIVE' as const,
    page,
    size,
  };

  const { data, isFetching, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['students', 'search', params],
    queryFn: () => studentsApi.search(params),
    placeholderData: (prev) => prev,
  });

  const { data: classSections } = useQuery({
    queryKey: ['setup', 'class-sections', yearId ?? 'none'],
    queryFn: () => setupApi.listClassSections(yearId ?? undefined),
    enabled: yearId != null,
    staleTime: 5 * 60 * 1000,
  });

  const onPickStudent = async (row: StudentSummary) => {
    if (!row.currentClassSectionId) {
      message.warning(`${row.firstName} has no current enrollment — enroll them first to collect fees.`);
      return;
    }
    setNavigating(row.id);
    try {
      const detail = await studentsApi.get(row.id);
      if (!detail.currentEnrollment) {
        message.warning('No active enrollment found.');
        return;
      }
      navigate(`/fee/cashier/${detail.currentEnrollment.id}`);
    } catch (e) {
      message.error(extractError(e));
    } finally {
      setNavigating(null);
    }
  };

  const columns: TableProps<StudentSummary>['columns'] = [
    {
      title: 'Reg #',
      dataIndex: 'registrationNumber',
      width: 120,
      render: (rn: string) => <Typography.Text code>{rn}</Typography.Text>,
    },
    {
      title: 'Name',
      render: (_, row) => (
        <Typography.Text strong>{row.firstName} {row.lastName}</Typography.Text>
      ),
    },
    {
      title: 'Class',
      width: 200,
      render: (_, row) =>
        row.currentClassName ? (
          <Tag>{row.currentClassName} · {row.currentSectionName}</Tag>
        ) : (
          <Typography.Text type="secondary">Not enrolled</Typography.Text>
        ),
    },
    {
      title: 'Primary guardian',
      render: (_, row) =>
        row.primaryGuardianName ? (
          <Space orientation="vertical" size={0}>
            <span>{row.primaryGuardianName}</span>
            {row.primaryGuardianPhone && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {row.primaryGuardianPhone}
              </Typography.Text>
            )}
          </Space>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: '',
      width: 110,
      align: 'right',
      render: (_, row) => (
        <Button
          size="small"
          type="primary"
          loading={navigating === row.id}
          disabled={!row.currentClassSectionId}
          onClick={(e) => {
            e.stopPropagation();
            onPickStudent(row);
          }}
        >
          Open
        </Button>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Fee"
        title="Collect payment"
        description="Find an actively-enrolled student and open their fee ledger to record a payment."
        count={data ? { value: data.totalElements, label: 'matching' } : undefined}
        onRefresh={() => refetch()}
        refreshKey={['students', 'search']}
        updatedAt={dataUpdatedAt}
      />

      <CashierTodayRail />

      <Space wrap size={12}>
        <Input
          ref={searchRef}
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search by name or reg #  (press / to focus)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={() => {
            const rows = data?.content ?? [];
            const first = rows.find((r) => r.currentClassSectionId);
            if (first) onPickStudent(first);
          }}
          style={{ width: 320 }}
        />
        <Select
          placeholder="Any class · section"
          allowClear
          value={classSectionId}
          onChange={setClassSectionId}
          options={(classSections ?? [])
            .slice()
            .sort((a, b) => a.classSortOrder - b.classSortOrder || a.sectionName.localeCompare(b.sectionName))
            .map((cs) => ({ value: cs.id, label: `${cs.className} · ${cs.sectionName}` }))}
          style={{ minWidth: 220 }}
          showSearch
          optionFilterProp="label"
        />
      </Space>

      {data && data.content.length === 0 && !isFetching ? (
        <Empty description="No active students match" />
      ) : (
        <Card styles={{ body: { padding: 0 } }}>
          <Table
            rowKey="id"
            size="middle"
            loading={isFetching}
            columns={columns}
            dataSource={data?.content ?? []}
            pagination={{
              current: (data?.number ?? page) + 1,
              pageSize: data?.size ?? size,
              total: data?.totalElements ?? 0,
              showSizeChanger: false,
              onChange: (next) => setPage(next - 1),
            }}
            onRow={(row) => ({
              onClick: () => onPickStudent(row),
              style: { cursor: 'pointer' },
            })}
          />
        </Card>
      )}

      <CashierQuickActions />
    </div>
  );
}
